import AccessTimeOutlined from "@mui/icons-material/AccessTimeOutlined";
import ArrowBack from "@mui/icons-material/ArrowBack";
import FlashOn from "@mui/icons-material/FlashOn";
import {
  Button,
  ButtonBase,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers";
import { type FC, useEffect } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import type { ISO8601_TIME } from "../../../utils/iso8601";
import { uuidv4 } from "../../../utils/uuid";
import { CustomRepeatEvery } from "../../event/components/custom-repeat-every";
import { CustomRepeatPeriod } from "../../event/components/custom-repeat-period";
import type { CustomRepeatType } from "../../event/hooks/use-number-of-repeats";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import { ScheduleTimezoneSelector } from "./schedule-form-v2-layout/schedule-timezone-selector";

type SurveyAlertWizardStep4Props = {
  onCompleteChange: (complete: boolean) => void;
  onBack: () => void;
};

const scheduleOptions = [
  {
    id: "immediate",
    label: "Send the notification immediately when a survey is submitted",
    Icon: FlashOn,
  },
  { id: "delayed", label: "Send after a delay", Icon: AccessTimeOutlined },
] as const;

type SurveyAlertWizardStep4Values = Pick<
  MomentFormMoment,
  "immediate" | "schedule" | "timezone" | "target_channel" | "trigger_survey_id"
>;

const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];

export const SurveyAlertWizardStep4: FC<SurveyAlertWizardStep4Props> = ({
  onBack,
  onCompleteChange,
}) => {
  const { control, setValue } = useFormContext<SurveyAlertWizardStep4Values>();
  const [immediate, schedule, timezone] = useWatch({
    control,
    name: ["immediate", "schedule", "timezone"],
  });

  const selectImmediate = (): void => {
    setValue("trigger_survey_id", null, { shouldDirty: true });
    setValue("immediate", true, { shouldDirty: true });
    setValue("schedule", null, { shouldDirty: true });
    setValue("timezone", null, { shouldDirty: true });
  };

  const selectDelayed = (): void => {
    setValue("trigger_survey_id", null, { shouldDirty: true });
    setValue("immediate", false, { shouldDirty: true });
    setValue(
      "schedule",
      {
        id: uuidv4(),
        send_at: "12:00" as ISO8601_TIME,
        event_initial: true,
        event_repeat: false,
        event: null,
        interval_json: { days: 1 },
        on_event_key: WEEKDAYS,
        dow: {
          monday: 1,
          tuesday: 1,
          wednesday: 1,
          thursday: 1,
          friday: 1,
          saturday: 0,
          sunday: 0,
        },
      },
      { shouldDirty: true },
    );
  };

  const delayPeriod = schedule?.interval_json
    ? (Object.keys(schedule.interval_json)[0] as keyof typeof schedule.interval_json | undefined)
    : undefined;
  const period = delayPeriod?.replace("s", "") as CustomRepeatType["period"];
  const amount = delayPeriod ? schedule?.interval_json?.[delayPeriod] : undefined;

  useEffect(() => {
    const delayedComplete = Boolean(
      schedule &&
      amount &&
      period &&
      schedule.send_at &&
      schedule.on_event_key !== undefined &&
      timezone !== undefined,
    );

    onCompleteChange(immediate || delayedComplete);
  }, [amount, immediate, onCompleteChange, period, schedule, timezone]);

  const updateDelay = (
    nextAmount: number | undefined,
    nextPeriod: CustomRepeatType["period"],
  ): void => {
    if (!schedule || !nextAmount || !nextPeriod) return;

    setValue("schedule.interval_json", { [`${nextPeriod}s`]: nextAmount }, { shouldDirty: true });
  };

  return (
    <>
      <DialogTitle>
        <Stack spacing={1} alignItems="flex-start">
          <Button
            startIcon={<ArrowBack />}
            onClick={onBack}
            data-analytics-id="survey-alert-wizard-back"
          >
            Back
          </Button>
          <Typography variant="h6" component="span">
            Set schedule
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body2">
            You can choose for the notification to be sent as soon as the survey is submitted, or
            after a delay.
          </Typography>
          <Stack spacing={2}>
            {scheduleOptions.map(({ id, label, Icon }) => {
              const selected = id === "immediate" ? immediate : !immediate && Boolean(schedule);

              return (
                <ButtonBase
                  key={id}
                  onClick={id === "immediate" ? selectImmediate : selectDelayed}
                  aria-pressed={Boolean(selected)}
                  data-analytics-id={`survey-alert-wizard-schedule-${id}`}
                  sx={{
                    width: "100%",
                    height: 68,
                    justifyContent: "flex-start",
                    gap: 1,
                    px: 2,
                    border: 1,
                    borderWidth: selected ? 3 : 1,
                    borderColor: selected ? "primary.main" : "divider",
                    borderRadius: 1,
                  }}
                >
                  <Icon sx={{ color: selected ? "primary.main" : "text.secondary" }} />
                  <Typography variant="body2">{label}</Typography>
                </ButtonBase>
              );
            })}
          </Stack>
          {!immediate && schedule && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">Send:</Typography>
                <CustomRepeatEvery
                  label="Time"
                  value={amount}
                  onChange={(value) => updateDelay(value, period)}
                />
                <CustomRepeatPeriod
                  label="Period"
                  value={period}
                  onChange={(value) => updateDelay(amount, value)}
                />
                <Typography variant="body2">After a survey is completed</Typography>
              </Stack>
              <Grid container spacing={1} alignItems="center">
                <Grid size={{ xs: 4 }}>
                  <Controller
                    name="schedule.send_at"
                    control={control}
                    render={({ field }) => (
                      <TimePicker
                        label="At"
                        value={
                          field.value ? new Date(`1970-01-01T${field.value.toString()}`) : null
                        }
                        onChange={(value) => {
                          if (!value) return field.onChange(undefined);

                          field.onChange(
                            `${value.getHours().toString().padStart(2, "0")}:${value
                              .getMinutes()
                              .toString()
                              .padStart(2, "0")}` as ISO8601_TIME,
                          );
                        }}
                        sx={{ width: "100%" }}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 8 }}>
                  <ScheduleTimezoneSelector disabled={false} label="Timezone" />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="schedule.on_event_key"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value === null}
                            onChange={(_, checked) => field.onChange(checked ? null : WEEKDAYS)}
                            slotProps={{ input: { "aria-label": "Allow sending on weekends" } }}
                          />
                        }
                        label="Allow sending on weekends (Saturday/Sunday)"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </>
  );
};
